<template>
  <div id="audio-player" class="audio-player" :class="{ playing: playing, visible: isVisible }">
    <!-- Minimal State -->
    <transition name="fade">
      <div v-show="isVisible" class="audio-player__minimal">
        <div class="minimal__main">
          <button class="minimal__nav" @click="previousTrack" :disabled="!tracks || tracks.length === 0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>
          <button class="minimal__toggle" @click="toggleMusic()">
            <pause-svg v-if="playing" />
            <play-svg v-else />
          </button>
          <button class="minimal__nav" @click="nextTrack" :disabled="!tracks || tracks.length === 0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
          <div class="minimal__info" ref="infoContainer">
            <div class="minimal__text">
              <span class="text__content" ref="textContent" :class="{ scrolling: shouldScroll }">
                <span class="text__title">{{ currentTrack?.title || 'Radio' }}</span>
                <span v-if="currentTrack?.description" class="text__description">
                  • {{ currentTrack.description }}</span
                >
              </span>
            </div>
          </div>
          <button class="minimal__close" @click="closePlayer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
            </svg>
          </button>
        </div>
        <div class="minimal__timeline">
          <audio-timeline :current-time="currentTime" :duration="duration" @seek="onSeek" />
        </div>
      </div>
    </transition>

    <audio
      ref="audio"
      v-if="currentTrack"
      :src="currentTrack.url"
      type="audio/mp3"
      loop
      volume="0.2"
      @timeupdate="onTimeUpdate"
      @canplaythrough="onCanPlayThrough"
      @ended="onTrackEnded"
    />
  </div>
</template>

<script lang="ts">
import PauseSvg from '@/assets/pause.svg';
import PlaySvg from '@/assets/play.svg';
import AudioTimeline from '@/components/media/AudioTimeline.vue';
import { Radio } from '@/types/api';
import { EventBus } from '@/utils/event-bus';
import gql from 'graphql-tag';
import { Component, Vue } from 'vue-property-decorator';

@Component({
  components: {
    PlaySvg,
    PauseSvg,
    AudioTimeline,
  },
  apollo: {
    tracks: {
      query: gql`
        query Tracks {
          tracks {
            id
            title
            description
            url
          }
        }
      `,
    },
  },
})
export default class AudioPlayer extends Vue {
  playing = false;
  duration = 0;
  currentTime = 0;
  currentTrackIndex = 0;
  tracks?: Radio[];
  isVisible = false;
  shouldScroll = false;

  get audioEl() {
    return this.$refs.audio as HTMLAudioElement | null;
  }

  get currentTrack(): Radio | undefined {
    if (!this.tracks || this.tracks.length === 0) return undefined;
    return this.tracks[this.currentTrackIndex];
  }

  get fullText(): string {
    const title = this.currentTrack?.title || 'Radio';
    const description = this.currentTrack?.description || '';
    return description ? `${title} • ${description}` : title;
  }

  async mounted() {
    EventBus.$on('toggle-music', this.toggleMusic);
    EventBus.$on('stop-music', this.stopMusic);
    EventBus.$on('start-music', this.startMusic);
    this.checkTextOverflow();
  }

  updated() {
    this.checkTextOverflow();
  }

  checkTextOverflow() {
    this.$nextTick(() => {
      const container = this.$refs.infoContainer as HTMLElement;
      const content = this.$refs.textContent as HTMLElement;
      if (container && content) {
        this.shouldScroll = content.scrollWidth > container.clientWidth;
        if (this.shouldScroll) {
          const overflow = content.scrollWidth - container.clientWidth;
          content.style.setProperty('--scroll-distance', `-${overflow}px`);
        }
      }
    });
  }

  public toggleMusic() {
    if (this.audioEl?.paused) {
      EventBus.$emit('start-music');
    } else {
      EventBus.$emit('stop-music');
    }
  }

  public async stopMusic() {
    if (this.playing) await this.audioEl?.pause();
    this.playing = false;
  }

  public async startMusic() {
    if (!this.playing) await this.audioEl?.play();
    this.playing = true;
    this.isVisible = true;
  }

  public closePlayer() {
    this.stopMusic();
    this.isVisible = false;
  }

  public onSeek(percent: number) {
    if (!this.audioEl) return;
    this.audioEl.currentTime = this.duration * percent;
  }

  onTimeUpdate() {
    if (!this.audioEl) return;
    this.currentTime = this.audioEl.currentTime;
  }

  onCanPlayThrough() {
    if (!this.audioEl) return;
    this.duration = this.audioEl.duration;
  }

  nextTrack() {
    if (!this.tracks || this.tracks.length === 0) return;
    this.currentTrackIndex = (this.currentTrackIndex + 1) % this.tracks.length;
    this.onTrackChange();
  }

  previousTrack() {
    if (!this.tracks || this.tracks.length === 0) return;
    this.currentTrackIndex = (this.currentTrackIndex - 1 + this.tracks.length) % this.tracks.length;
    this.onTrackChange();
  }

  onTrackEnded() {
    // Auto-play next track when current one ends
    this.nextTrack();
  }

  async onTrackChange() {
    const wasPlaying = this.playing;
    if (wasPlaying) {
      this.audioEl?.pause();
    }
    // Reset time
    this.currentTime = 0;
    // Wait for new source to load
    await this.$nextTick();
    if (wasPlaying && this.audioEl) {
      await this.audioEl.play();
    }
  }

  async selectTrack(index: number) {
    if (index === this.currentTrackIndex) return;
    this.currentTrackIndex = index;
    await this.onTrackChange();
  }
}
</script>

<style lang="scss" scoped>
.audio-player {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 6;
  pointer-events: none;

  // Minimal State
  &__minimal {
    position: fixed;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 480px;
    max-width: 90vw;
    background: $menu-item-background;
    border: 2px solid $color-theme-yellow;
    border-radius: 8px;
    box-shadow: $menu-item-box-shadow;
    pointer-events: all;
    transition: all 0.3s ease;
    overflow: hidden;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);

      .minimal__timeline {
        margin-bottom: 6px;
        max-height: 24px;
        opacity: 1;
        padding: 6px 12px;
      }
    }

    .minimal__main {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      gap: 8px;
    }

    .minimal__nav {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      color: $color-theme-yellow;
      flex-shrink: 0;

      svg {
        width: 14px;
        height: 14px;
      }

      &:hover:not(:disabled) {
        background: $color-theme-red;
        color: white;
        transform: scale(1.05);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }

    .minimal__toggle {
      width: 32px;
      height: 32px;
      border: none;
      background: $color-theme-yellow;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;

      svg {
        width: 14px;
        height: 14px;
        fill: black;
      }

      &:hover {
        background: $color-theme-red;

        svg {
          fill: white;
        }
      }
    }

    .minimal__info {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      position: relative;

      .minimal__text {
        font-weight: bold;
        font-size: 13px;
        color: $color-theme-yellow;
        white-space: nowrap;
        overflow: hidden;
        position: relative;

        .text__content {
          display: inline-flex;
          gap: 4px;
          align-items: flex-end;

          &.scrolling {
            animation: pan-text 20s linear infinite;
          }

          .text__description {
            font-size: 12px;
            color: #666;
          }
        }
      }
    }

    .minimal__close {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      color: $color-theme-yellow;
      flex-shrink: 0;

      svg {
        width: 14px;
        height: 14px;
      }

      &:hover {
        background: $color-theme-red;
        color: white;
        transform: scale(1.05);
      }
    }

    .minimal__timeline {
      max-height: 0;
      opacity: 0;
      padding: 0 12px;
      transition: all 0.3s ease;
    }
  }
}

@keyframes pan-text {
  0%,
  20% {
    transform: translateX(0);
  }
  45% {
    transform: translateX(var(--scroll-distance, -100px));
  }
  55% {
    transform: translateX(var(--scroll-distance, -100px));
  }
  80%,
  100% {
    transform: translateX(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}
</style>
